/**
 * Admin dashboard — two modes (Company / Person) + a date-range
 * picker shared across both modes.
 *
 * The range picker lives in the shell so switching modes preserves
 * the selected time window. Default range = current month only,
 * which keeps the existing single-month behaviour for anyone who
 * doesn't touch the picker.
 *
 * The mode toggle is rendered as a prominent segmented control so
 * it's impossible to miss — early feedback was that the previous
 * inline buttons looked decorative rather than functional.
 */
import { useState, useMemo } from "preact/hooks";
import type { JSX } from "preact";
import { MONTHS } from "../config";
import { DashboardCompany } from "./DashboardCompany";
import { DashboardPerson } from "./DashboardPerson";

type DashboardMode = "company" | "person";

export interface DateRange {
  startYear: number;
  startMonth: number; // 0-indexed
  endYear: number;
  endMonth: number; // 0-indexed
}

/** Years to offer in the dropdowns — 3 years back, 1 year forward
 *  from the currently selected month. Captures the test campaign
 *  + any roll-forward to next year's planning without bloating
 *  the picker to a giant scroll list. */
function buildYearOptions(currentYearValue: number): number[] {
  const out: number[] = [];
  for (let y = currentYearValue - 3; y <= currentYearValue + 1; y++) {
    out.push(y);
  }
  return out;
}

/** Shift a date range backwards by its own length so we can compute
 *  the "prior period" delta. For a single-month range, this yields
 *  the previous calendar month. For Jan–Mar 2026 it yields Oct–Dec
 *  2025. */
export function priorRange(r: DateRange): DateRange {
  const startKey = r.startYear * 12 + r.startMonth;
  const endKey = r.endYear * 12 + r.endMonth;
  const length = endKey - startKey + 1; // months in current range
  const newEndKey = startKey - 1;
  const newStartKey = newEndKey - length + 1;
  return {
    startYear: Math.floor(newStartKey / 12),
    startMonth: ((newStartKey % 12) + 12) % 12,
    endYear: Math.floor(newEndKey / 12),
    endMonth: ((newEndKey % 12) + 12) % 12,
  };
}

export function rangeLabel(r: DateRange): string {
  const startLabel = `${MONTHS[r.startMonth]} ${r.startYear}`;
  if (
    r.startYear === r.endYear &&
    r.startMonth === r.endMonth
  ) {
    return startLabel;
  }
  return `${startLabel} → ${MONTHS[r.endMonth]} ${r.endYear}`;
}

export function rangeMonthCount(r: DateRange): number {
  return r.endYear * 12 + r.endMonth - (r.startYear * 12 + r.startMonth) + 1;
}

export function Dashboard(): JSX.Element {
  const [mode, setMode] = useState<DashboardMode>("company");

  // Self-contained: the Dashboard is decoupled from the shared month bar
  // (hidden on this tab), so it anchors on the ACTUAL current month rather
  // than the app's globally-selected month.
  const [range, setRange] = useState<DateRange>(() => {
    const now = new Date();
    const ty = now.getFullYear();
    const tm = now.getMonth();
    return { startYear: ty, startMonth: tm, endYear: ty, endMonth: tm };
  });

  const yearOptions = useMemo(() => buildYearOptions(new Date().getFullYear()), []);

  const onPreset = (preset: "this-month" | "last-3m" | "ytd"): void => {
    const now = new Date();
    const ty = now.getFullYear();
    const tm = now.getMonth();
    if (preset === "this-month") {
      setRange({ startYear: ty, startMonth: tm, endYear: ty, endMonth: tm });
    } else if (preset === "last-3m") {
      // 3-month window ending at the current month
      const endKey = ty * 12 + tm;
      const startKey = endKey - 2;
      setRange({
        startYear: Math.floor(startKey / 12),
        startMonth: ((startKey % 12) + 12) % 12,
        endYear: ty,
        endMonth: tm,
      });
    } else {
      // YTD: January of the current year through the current month
      setRange({ startYear: ty, startMonth: 0, endYear: ty, endMonth: tm });
    }
  };

  // Guard against the user picking an inverted range — flip start/end
  // before passing down so child views never see startKey > endKey.
  const normalizedRange: DateRange = useMemo(() => {
    const startKey = range.startYear * 12 + range.startMonth;
    const endKey = range.endYear * 12 + range.endMonth;
    if (startKey <= endKey) return range;
    return {
      startYear: range.endYear,
      startMonth: range.endMonth,
      endYear: range.startYear,
      endMonth: range.startMonth,
    };
  }, [range]);

  // Single-month stepper — the fast common case. Moves to the adjacent
  // month, collapsing any multi-month range to that single month.
  const stepMonth = (delta: number): void => {
    const key = normalizedRange.startYear * 12 + normalizedRange.startMonth + delta;
    const yr = Math.floor(key / 12);
    const mo = ((key % 12) + 12) % 12;
    setRange({ startYear: yr, startMonth: mo, endYear: yr, endMonth: mo });
  };

  return (
    <div class="dashboard-view">
      <div class="dashboard-controls">
        {/* Bigger, more prominent mode toggle — full-width segmented
            control with icons so the two views are immediately
            discoverable. */}
        <div
          class="dashboard-modebar dashboard-modebar-big"
          role="tablist"
          aria-label="Dashboard view"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === "company"}
            class={`dashboard-mode-big${mode === "company" ? " active" : ""}`}
            onClick={() => setMode("company")}
          >
            <span class="dashboard-mode-ico" aria-hidden="true">🏢</span>
            <span class="dashboard-mode-text">
              <span class="dashboard-mode-title">Company</span>
              <span class="dashboard-mode-sub">All teams · aggregated</span>
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "person"}
            class={`dashboard-mode-big${mode === "person" ? " active" : ""}`}
            onClick={() => setMode("person")}
          >
            <span class="dashboard-mode-ico" aria-hidden="true">👤</span>
            <span class="dashboard-mode-text">
              <span class="dashboard-mode-title">Person</span>
              <span class="dashboard-mode-sub">One teammate · deep dive</span>
            </span>
          </button>
        </div>

        {/* Date picker — a fast single-month stepper + quick presets, with
            the full From→To range tucked behind a "Custom range" disclosure.
            The selected window applies to every widget in both modes. */}
        <div class="dashboard-range" aria-label="Date range">
          <div class="dash-month-stepper">
            <button
              type="button"
              class="dash-step"
              onClick={() => stepMonth(-1)}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span class="dash-step-label" aria-live="polite">{rangeLabel(normalizedRange)}</span>
            <button
              type="button"
              class="dash-step"
              onClick={() => stepMonth(1)}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          <div class="dashboard-range-presets">
            <button type="button" class="range-preset" onClick={() => onPreset("this-month")}>
              This month
            </button>
            <button type="button" class="range-preset" onClick={() => onPreset("last-3m")}>
              Last 3 months
            </button>
            <button type="button" class="range-preset" onClick={() => onPreset("ytd")}>
              Year to date
            </button>
          </div>
          <details class="dash-custom">
            <summary>Custom range</summary>
            <div class="dashboard-range-grid">
              <span class="dashboard-range-label">From</span>
              <RangeSelect
                year={normalizedRange.startYear}
                month={normalizedRange.startMonth}
                yearOptions={yearOptions}
                onChange={(yr, mo) =>
                  setRange((r) => ({ ...r, startYear: yr, startMonth: mo }))
                }
                ariaLabel="Range start"
              />
              <span class="dashboard-range-label">to</span>
              <RangeSelect
                year={normalizedRange.endYear}
                month={normalizedRange.endMonth}
                yearOptions={yearOptions}
                onChange={(yr, mo) =>
                  setRange((r) => ({ ...r, endYear: yr, endMonth: mo }))
                }
                ariaLabel="Range end"
              />
            </div>
          </details>
        </div>
      </div>

      {mode === "company" ? (
        <DashboardCompany range={normalizedRange} />
      ) : (
        <DashboardPerson range={normalizedRange} />
      )}
    </div>
  );
}

function RangeSelect({
  year,
  month,
  yearOptions,
  onChange,
  ariaLabel,
}: {
  year: number;
  month: number;
  yearOptions: number[];
  onChange: (year: number, month: number) => void;
  ariaLabel: string;
}): JSX.Element {
  return (
    <span class="dashboard-range-select" aria-label={ariaLabel}>
      <select
        value={String(month)}
        onChange={(e) =>
          onChange(year, Number((e.target as HTMLSelectElement).value))
        }
      >
        {MONTHS.map((label, idx) => (
          <option key={idx} value={String(idx)}>
            {label}
          </option>
        ))}
      </select>
      <select
        value={String(year)}
        onChange={(e) =>
          onChange(Number((e.target as HTMLSelectElement).value), month)
        }
      >
        {yearOptions.map((y) => (
          <option key={y} value={String(y)}>
            {y}
          </option>
        ))}
      </select>
    </span>
  );
}
